import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Association } from '../../../../../../models/association';
import { AssociationService } from '../../../../../../services/association/association.service';
import { batchAll, CallbackResult, CallbackResultStatus, searchList } from '../../../../../../utils';

@Component({
  selector: 'app-bulk-add-customers-to-association-modal',
  templateUrl: './bulk-add-customers-to-association-modal.component.html',
  styleUrls: ['./bulk-add-customers-to-association-modal.component.css']
})
export class BulkAddCustomersToAssociationModalComponent {

  @Input() customerIds: number[];
  @Input() associations: Association[];
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public associationSearchInput = '';
  public selectedAssociation: Association = null;

  public errorMessage = '';

  public loadingSave = false;
  public numberSaved = 0;

  constructor(
    public activeModal: NgbActiveModal,
    public associationService: AssociationService
  ) {}

  public getMatchedAssociations(): Association[] {
    return searchList<Association>(
      this.associationSearchInput,
      this.associations.map(association => {
        return {value: association, searchableText: association.name};
      })
    );
  }

  public selectAssociation(association: Association) {
    this.selectedAssociation = association;
    this.associationSearchInput = association.name;
  }

  /** Resolves once the whole batch is done; the template ignores the promise. */
  public handleSubmit(): Promise<void> {
    if (this.selectedAssociation === null) { return Promise.resolve(); }

    const association = this.selectedAssociation;

    this.errorMessage = '';
    this.loadingSave = true;

    this.numberSaved = 0;
    let successfulCount = 0;
    let superfluousCount = 0;
    let failedCount = 0;
    // Set by the first failure that makes the rest of the run futile, which
    // also stops it. Explains the stop in the reported message.
    let stopReason = '';

    const customerIdHandler = (customerId: number): Promise<void> => {
      if (stopReason !== '') { return Promise.resolve(); }

      return this.associationService
        .addCustomerToAssociation(association.id, customerId)
        .then(() => { successfulCount++; })
        .catch(error => {
          if (error.status === 400) {
            // For a customer of this org, the endpoint's only 400 is that they
            // are already a member — capitan-server
            // `CreateAssociationMemberTestCase`.
            superfluousCount++;
            return;
          }

          failedCount++;

          if (error.status === 403) {
            stopReason = 'You do not have permission to perform this operation.';
          } else if (error.status >= 500) {
            stopReason = 'The server returned an error ('
              + error.status.toString() + ').';
          } else if (error.status === 0) {
            stopReason = 'The connection to Capitan was lost.';
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    return batchAll(
      4,
      this.customerIds,
      customerIdHandler
    )
      .then(() => {
        const notAttemptedCount = this.customerIds.length
          - (successfulCount + superfluousCount + failedCount);

        const parts: string[] = [];
        if (successfulCount > 0) {
          parts.push(successfulCount.toString() + ' customer'
            + (successfulCount > 1 ? 's' : '') + ' successfully added to '
            + association.name + '.');
        }
        if (superfluousCount > 0) {
          parts.push(superfluousCount.toString() + (superfluousCount > 1
            ? ' customers were already members.'
            : ' customer was already a member.'));
        }
        if (failedCount > 0) {
          parts.push(failedCount.toString() + ' customer'
            + (failedCount > 1 ? 's' : '') + ' could not be added.');
        }
        if (notAttemptedCount > 0) {
          parts.push(notAttemptedCount.toString() + ' customer'
            + (notAttemptedCount > 1 ? 's were' : ' was') + ' not attempted.');
        }
        if (stopReason !== '') { parts.push(stopReason); }

        const message = parts.join(' ');

        // Anything short of "every selected customer is now a member" is
        // reported in the modal, which stays open, rather than as the parent's
        // success alert — a partial run must not read as a completed one.
        if ((failedCount > 0) || (notAttemptedCount > 0)) {
          this.errorMessage = message;
          return;
        }

        this.savedCallback({
          text: message,
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  public saveButtonEnabled() {
    return (
      (this.selectedAssociation !== null) &&
      !this.loadingSave
    );
  }

  public getLoadingProgressPercentageStr() {
    return Math.ceil((this.numberSaved / this.customerIds.length) * 100).toString();
  }

}
